import { IShimmerElement, Shimmer, ShimmerElementType } from '@fluentui/react';
import { makeStyles, tokens, webLightTheme } from '@fluentui/react-components';
import * as React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import devBoxCardPreviewWin11Image_Dark from '../../content/images/dev-box-preview-win11_dark.jpg';
import devBoxCardPreviewWin11Image_Light from '../../content/images/dev-box-preview-win11_light.jpg';
import { useCurrentFluent2Theme } from '../../hooks/styling';
import { CssSelector } from '../../themes/constants/css-selectors';
import WindowsProductImage from '../../components/dev-box/dev-box-card/windows-product-image';

const messages = defineMessages({
    loadingAriaLabel: {
        id: 'HomeLoadingState_Loading_AriaLabel',
        defaultMessage: 'Loading your resources',
        description: 'Accessible label for the home page resource loading indicator.',
    },
    loadedMessage: {
        id: 'HomeLoadingState_LoadedMessage_Text',
        defaultMessage: 'Resource loading is complete.',
        description:
            'Accessible announcement that the home page finished its initial resource loading operation. Some resources might not be available.',
    },
    sectionTitle: {
        id: 'HomeLoadingState_SectionTitle_Text',
        defaultMessage: 'Your resources',
        description: 'Heading shown while the home page discovers the signed-in user’s resources.',
    },
});

const screenReaderOnlyStyles: React.CSSProperties = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
};

const useLoadingStyles = makeStyles({
    section: {
        alignItems: 'flex-start',
        display: 'flex',
        flexDirection: 'column',
        gap: '26px',
        width: '100%',
    },
    heading: {
        fontSize: '16px',
        fontWeight: 600,
        margin: 0,
        textAlign: 'left',
        width: '100%',
    },
    card: {
        backgroundColor: tokens.colorNeutralBackground1,
        border: 'none',
        borderRadius: '4px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        height: '406px',
        outline: 'none',
        overflow: 'hidden',
        padding: 0,
        width: '260px',

        [CssSelector.ScreenSizeSmallAndBelow]: {
            width: '100%',
        },
    },
    preview: {
        height: '184px',
        position: 'relative',
        width: '100%',
    },
    previewImage: {
        height: '100%',
        objectFit: 'cover',
        opacity: 0.55,
        width: '100%',
    },
    productIcon: {
        alignItems: 'center',
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: '2px',
        bottom: '6px',
        display: 'flex',
        height: '32px',
        justifyContent: 'center',
        left: '6px',
        position: 'absolute',
        width: '32px',
    },
    details: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        padding: '16px',
    },
    nameLine: {
        width: '35%',
    },
    projectLine: {
        width: '55%',
    },
    stateLine: {
        width: '25%',
    },
});

const nameShimmer: IShimmerElement[] = [{ type: ShimmerElementType.line, height: 20, width: '100%' }];
const projectShimmer: IShimmerElement[] = [{ type: ShimmerElementType.line, height: 14, width: '100%' }];
const stateShimmer: IShimmerElement[] = [{ type: ShimmerElementType.line, height: 16, width: '100%' }];

const HomeResourcePlaceholder: React.FC = () => {
    const styles = useLoadingStyles();
    const theme = useCurrentFluent2Theme();
    const previewImage = theme === webLightTheme ? devBoxCardPreviewWin11Image_Light : devBoxCardPreviewWin11Image_Dark;

    return (
        <div className={styles.card} style={{ backgroundColor: theme.colorNeutralBackground1 }}>
            <div className={styles.preview}>
                <img alt="" className={styles.previewImage} src={previewImage} />
                <div className={styles.productIcon}>
                    <WindowsProductImage />
                </div>
            </div>
            <div className={styles.details}>
                <div className={styles.nameLine}>
                    <Shimmer shimmerElements={nameShimmer} />
                </div>
                <div className={styles.projectLine}>
                    <Shimmer shimmerElements={projectShimmer} />
                </div>
                <div className={styles.stateLine}>
                    <Shimmer shimmerElements={stateShimmer} />
                </div>
            </div>
        </div>
    );
};

interface HomeLoadingAnnouncementProps {
    isLoading: boolean;
}

export const HomeLoadingAnnouncement: React.FC<HomeLoadingAnnouncementProps> = (
    props: HomeLoadingAnnouncementProps
) => {
    const { isLoading } = props;
    const { formatMessage } = useIntl();
    const hasAnnouncedLoading = React.useRef(false);
    const [announcement, setAnnouncement] = React.useState('');

    React.useEffect(() => {
        if (isLoading) {
            hasAnnouncedLoading.current = true;
            setAnnouncement(formatMessage(messages.loadingAriaLabel));
        } else if (hasAnnouncedLoading.current) {
            setAnnouncement(formatMessage(messages.loadedMessage));
        }
    }, [formatMessage, isLoading]);

    return (
        <span aria-live="polite" style={screenReaderOnlyStyles}>
            {announcement}
        </span>
    );
};

export const HomeLoadingState: React.FC = () => {
    const styles = useLoadingStyles();

    return (
        <section className={styles.section}>
            <h2 className={styles.heading}>
                <FormattedMessage {...messages.sectionTitle} />
            </h2>
            <div aria-hidden={true}>
                <HomeResourcePlaceholder />
            </div>
        </section>
    );
};

export default HomeLoadingState;
